import { useEffect, useState } from "react";

export interface SelectedFields<T extends unknown> {
    [key: string]: T | undefined;
}

export type PossibleOptions<T extends unknown> = SelectedFields<T>[];

export interface FilterOptions<T extends unknown> {
    [key: string]: T[] | undefined;
}

const FilterFields = <T extends unknown>(
    optionsCriteria: PossibleOptions<T> | undefined,
    selectedFields: SelectedFields<T>[]
): FilterOptions<T>[] => {
    const [filteredOptions, setFilteredOptions] = useState<FilterOptions<T>[]>([{}])
    const [currentSelected, setCurrentSelected] = useState<SelectedFields<T>>({})
    const [selectedPrevState, setSelectedPrevState] = useState<SelectedFields<T>>({})
    const [lastModified, setLastModified] = useState<string | null>('')

    useEffect(() => {
        const filterFieldsOptions = (fields: any, index: number) => {
            const filteredOptionsTemp: FilterOptions<T> = {};
            if (!optionsCriteria || !Array.isArray(optionsCriteria)) {
                return filteredOptionsTemp;
            }

            optionsCriteria.forEach((opt) => {
                let isMatch = true;
                Object.keys(fields).forEach((selectedFieldKey) => {
                    const attrValue = opt[selectedFieldKey];
                    const selectedValue = fields[selectedFieldKey];

                    if (attrValue !== selectedValue && selectedValue !== undefined && selectedValue !== '') {
                        isMatch = false;
                    }
                });

                if (isMatch) {
                    Object.keys(opt).forEach((attrKey) => {
                        const value = opt[attrKey] as T;

                        if (!fields[attrKey] && !filteredOptionsTemp[attrKey] && fields[attrKey] !== undefined) {
                            filteredOptionsTemp[attrKey] = [value];
                        } else if (!fields[attrKey] && !filteredOptionsTemp[attrKey]?.includes(value)) {
                            filteredOptionsTemp[attrKey]?.push(value);
                        }
                    });
                }
            });
            setFilteredOptions((prevFilteredOptions: any) => {
                // Avoid unnecessary updates by checking if the new value differs
                const newFilteredOptions = selectedFields.map((fieldsData:any, i:number) => {
                    if (i === index) {
                        if (JSON.stringify(prevFilteredOptions[i]) !== JSON.stringify(filteredOptionsTemp)) {
                            setCurrentSelected(fields); // Update only when needed
                            return filteredOptionsTemp; // Replace the updated object
                        }
                    }
                    return prevFilteredOptions[i]; // Return unchanged objects
                });

                // If no changes, return the same array to avoid re-render
                if (JSON.stringify(newFilteredOptions) === JSON.stringify(prevFilteredOptions)) {
                    return prevFilteredOptions; // No changes, avoid unnecessary state update
                }

                return newFilteredOptions; // Return the updated array
            });
        };
        selectedFields.map((field, index) => filterFieldsOptions(field, index));
    }, [optionsCriteria, selectedFields, lastModified]);

    return filteredOptions;

};

export default FilterFields;